import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { PRODUCTS } from '@podstack/ui';

interface ProductSwitcherProps {
  /** Kept for callers; the console brand now reads "Podstack Cloud". */
  subtitle?: string;
  compact?: boolean;
  /** Extra classes for the brand name (e.g. hide it in the icon rail). */
  nameClassName?: string;
}

/** Console brand: real Podstack logo + "Podstack Cloud" (matches customer-portal's Logo). */
export const Logo: React.FC<{ nameClassName?: string }> = ({ nameClassName = '' }) => (
  <>
    <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" aria-hidden="true" className="w-7 h-7 flex-none" />
    <span className={`font-display font-semibold text-[17px] text-ps-text whitespace-nowrap ${nameClassName}`}>
      Podstack Cloud
    </span>
  </>
);

// Product switcher: same product list and behaviour as the shared
// @podstack/ui ProductSwitcher (current product marked and not a link,
// others navigate to that product), with the console brand as its trigger.
const ProductSwitcher: React.FC<ProductSwitcherProps> = ({ compact = false, nameClassName }) => {
  const products = PRODUCTS.filter((p) => p.show);
  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label="Switch product"
        className={`flex items-center gap-2.5 rounded-md py-1 pr-1.5 transition-colors hover:bg-ps-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent ${
          compact ? 'min-h-[44px]' : ''
        }`}
      >
        <Logo nameClassName={nameClassName} />
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="z-50 mt-2 w-72 rounded-xl border border-ps-strong bg-ps-card py-1 shadow-xl shadow-black/50 focus:outline-none"
      >
        <div className="border-b border-ps-border px-4 py-3">
          <p className="text-sm font-medium text-ps-text">Podstack products</p>
        </div>
        {products.map((product) => {
          const Icon = product.icon;
          const isCurrent = product.key === 'inference';
          const content = (
            <>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ps-raised">
                <Icon className="h-5 w-5 text-ps-accent" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-ps-text">{product.name}</span>
                  {isCurrent && <span className="ps-tag ps-tag-accent">Current</span>}
                </span>
                <span className="block truncate text-xs text-ps-subtle">{product.description}</span>
              </span>
            </>
          );
          const cls = 'flex w-full items-center gap-3 px-4 py-2.5 text-left data-[focus]:bg-ps-raised';
          return (
            <MenuItem key={product.key} disabled={isCurrent}>
              {isCurrent ? (
                <div className={`${cls} bg-ps-raised/50`}>{content}</div>
              ) : (
                <a href={product.href} className={cls}>
                  {content}
                </a>
              )}
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
};

export default ProductSwitcher;
